import React from 'react';
import { Dialog, DialogTitle, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CloseIcon from '@mui/icons-material/Close';
import * as api from '../plannerApi';
import * as T from '../types';
import { nearDay, quantity, recurringFrequencyText } from '../labels';
import { useNotify, useRun } from '../common';

/** "gastas ~1 L/dia (de 6 entradas) · acaba ~qui 29", as Android's StockItem.RhythmText; null when there is neither. */
export function rhythmText(item: T.StockItem): string | null {
  const parts: string[] = [];
  if (item.rhythm) parts.push(`gastas ~${quantity(item.rhythm.perDay, item.unit)}/dia (de ${item.rhythm.purchases} entradas)`);
  if (item.runsOutOn) parts.push(`acaba ~${nearDay(item.runsOutOn)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * "Ajustar ao ritmo": what the rhythm learnt from the purchases suggests (a daily use, a minimum, or the minimum
 * instead of a regular purchase), one tap each; "Não" stops suggesting for this item. In the style of "Passo a prever?".
 */
export const RhythmDialog: React.FC<{ item: T.StockItem | null; onClose: (updated: T.StockItem | null) => void }> = ({ item, onClose }) => {
  const run = useRun();
  const notify = useNotify();
  const suggestions = item?.rhythmSuggestions;
  if (!item || !suggestions) return null;

  const apply = async (kind: T.RhythmSuggestionKind, done: string) => {
    const updated = await run(() => api.applyRhythmSuggestion(item.catalogItemPublicId, kind));
    if (updated) notify(`${item.name}: ${done}`, { severity: 'success' });
    onClose(updated ?? null);
  };

  const dismiss = async () => {
    const updated = await run(() => api.dismissRhythmSuggestions(item.catalogItemPublicId));
    onClose(updated ?? null);
  };

  const { dailyUse, minStock, replacesRecurring } = suggestions;
  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>Ajustar {item.name} ao ritmo?</DialogTitle>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pt: 0.5 }}>{rhythmText(item)}</Typography>
      <List>
        {dailyUse !== null && dailyUse !== undefined && (
          <ListItemButton onClick={() => apply('DailyUse', 'gasto por dia')}>
            <ListItemIcon><EventRepeatIcon color="primary" /></ListItemIcon>
            <ListItemText primary="Gasto por dia" secondary={`Conta ~${quantity(dailyUse, item.unit)}/dia até à próxima ida`} />
          </ListItemButton>
        )}
        {replacesRecurring && minStock !== null && minStock !== undefined ? (
          <ListItemButton onClick={() => apply('ReplaceRecurring', 'mínimo em vez da compra regular')}>
            <ListItemIcon><Inventory2Icon /></ListItemIcon>
            <ListItemText primary="Trocar a compra regular" secondary={`${recurringFrequencyText(replacesRecurring)} → mínimo de ${quantity(minStock, item.unit)}`} />
          </ListItemButton>
        ) : minStock !== null && minStock !== undefined && (
          <ListItemButton onClick={() => apply('MinStock', 'mínimo em casa')}>
            <ListItemIcon><Inventory2Icon /></ListItemIcon>
            <ListItemText primary="Mínimo em casa" secondary={`Junta à lista quando houver menos de ${quantity(minStock, item.unit)}`} />
          </ListItemButton>
        )}
        <ListItemButton onClick={dismiss}>
          <ListItemIcon><CloseIcon /></ListItemIcon>
          <ListItemText primary="Não, não sugiras mais" />
        </ListItemButton>
      </List>
    </Dialog>
  );
};
